import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import * as CS from '../section.styles';
import ServiceItemForm from './service-item-form';
import { BaseButton } from '../../../base';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface IServicesListProps {
    claimEditorStore: ClaimEditorStore;
}

export const ServicesList: React.FunctionComponent<IServicesListProps> = observer(({ claimEditorStore }) => {
    const items = useMemo<any[]>(() => {
        const fromStore = claimEditorStore?.services;
        if (fromStore && Array.isArray(fromStore)) return fromStore;
        const payload: any = claimEditorStore?.activePayload;
        return Array.isArray(payload?.services) ? payload.services : [];
    }, [claimEditorStore?.services, claimEditorStore?.activePayload]);

    const handleAdd = (): void => {
        const current = Array.isArray(claimEditorStore.services) ? claimEditorStore.services : [];
        const next = [...current, {
            procedureCode: '',
            diagnosisCodes: [],
            emergency: false
        }];
        claimEditorStore.updateServices(next);
    };
    const handleRemove = (index: number): void => {
        const current = Array.isArray(claimEditorStore.services) ? claimEditorStore.services : [];
        const next = current.filter((_, i) => i !== index);
        claimEditorStore.updateServices(next);
    };
    const handleCopy = (index: number): void => {
        claimEditorStore.copyServiceLine(index);
    };

    return (
        <div>
            {claimEditorStore.validationSignal > 0 && items.length === 0 && (
                <FormErrorBanner messages={["At least one service line is required"]} />
            )}
            {items.map((item, idx) => (
                <CS.SectionContainer key={idx}>
                    <CS.SectionHeader>
                        <CS.SectionTitle>{`Service ${idx + 1}`}</CS.SectionTitle>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <BaseButton variant="outlined" onClick={(): void => handleCopy(idx)}>Copy Service Line</BaseButton>
                            <BaseButton variant="warning" onClick={(): void => handleRemove(idx)}>✕ Remove Service</BaseButton>
                        </div>
                    </CS.SectionHeader>
                    <ServiceItemForm 
                        claimEditorStore={claimEditorStore}
                        serviceIndex={idx}
                        initialCodes={item?.diagnosisCodes}
                        initialPlaceOfService={item?.placeOfServiceType || item?.placeOfService}
                        initialPrimaryAuth={item?.primaryPriorAuthorizationNumber}
                        initialSecondaryAuth={item?.secondaryPriorAuthorizationNumber}
                        initialTertiaryAuth={item?.tertiaryPriorAuthorizationNumber}
                        initialNarrative={item?.narrative}
                        initialEmergency={!!item?.emergency}
                        initialFromDos={item?.fromDateOfService}
                        initialThroughDos={item?.throughDateOfService}
                        initialProcedureCode={item?.procedureCode}
                        initialMod1={item?.procedureModifierCodes?.[0]}
                        initialMod2={item?.procedureModifierCodes?.[1]}
                        initialMod3={item?.procedureModifierCodes?.[2]}
                        initialMod4={item?.procedureModifierCodes?.[3]}
                        initialUnits={item?.units}
                        initialDrugQuantity={item?.drug?.x12DrugQuantity}
                        initialDrugNdc={item?.drug?.x12NationalDrugCode}
                        initialDrugMeasurement={item?.drug?.x12DrugMeasurement}
                    />
                </CS.SectionContainer>
            ))}

            <CS.ButtonContainer>
                <BaseButton variant="primary" onClick={handleAdd}>+ Add Service</BaseButton>
            </CS.ButtonContainer>
        </div>
    );
});

export default ServicesList;


